import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { NumberInput } from '@/components/ui/number-input';
import { Combobox } from '@/components/ui/combobox';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';

interface AddMovementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // Fixed counterparty (per-cari ledger). Omit both to show a cari picker.
  source?: 'customer' | 'supplier';
  id?: string;
  counterpartyName?: string;
}

const CURRENCIES = ['TRY', 'EUR', 'GBP', 'USD'] as const;
const selectClass =
  'h-8 w-full border border-border bg-background px-2.5 text-sm focus:outline-none focus:border-foreground focus:ring-1 focus:ring-foreground/20';

export function AddMovementDialog({ open, onOpenChange, source, id, counterpartyName }: AddMovementDialogProps) {
  const { t } = useTranslation('trading');
  const today = new Date().toISOString().slice(0, 10);
  const fixed = !!source && !!id;

  const [cariValue, setCariValue] = useState(''); // `${source}:${id}`
  const [date, setDate] = useState(today);
  const [voucherNo, setVoucherNo] = useState('');
  const [description, setDescription] = useState('');
  const [currency, setCurrency] = useState<string>('TRY');
  const [direction, setDirection] = useState<'debit' | 'credit'>('debit');
  const [amount, setAmount] = useState<number | undefined>(undefined);
  const [tryValue, setTryValue] = useState<number | undefined>(undefined);

  const isTRY = currency === 'TRY';
  const utils = trpc.useUtils();

  // Cari picker — only needed when no fixed counterparty was passed.
  const { data: cariList } = trpc.tradingCari.list.useQuery(
    { all: true },
    { enabled: open && !fixed }
  );
  const cariOptions = (cariList?.items ?? []).map((c) => ({
    value: `${c.source}:${c.id}`,
    label: c.legacyCariCode ? `${c.companyName} · ${c.legacyCariCode}` : c.companyName,
    description: c.cariType,
    keywords: [c.companyName, c.legacyCariCode ?? ''],
  }));

  const reset = () => {
    setCariValue('');
    setDate(today);
    setVoucherNo('');
    setDescription('');
    setCurrency('TRY');
    setDirection('debit');
    setAmount(undefined);
    setTryValue(undefined);
  };

  const create = trpc.tradingCari.createMovement.useMutation({
    onSuccess: () => {
      utils.tradingCari.movements.invalidate();
      utils.tradingCari.allMovements.invalidate();
      utils.tradingCari.balances.invalidate();
      utils.tradingCari.cariMizan.invalidate();
      showSuccessToast(t('cariMovements.created', 'Movement added'));
      reset();
      onOpenChange(false);
    },
    onError: (e) => showErrorToast(e.message),
  });

  const hasCounterparty = fixed || !!cariValue;
  const canSubmit =
    hasCounterparty && !!voucherNo && !!amount && amount > 0 && (isTRY || (tryValue ?? 0) >= 0);

  const submit = () => {
    if (!canSubmit || !amount) return;
    const [pickedSource, pickedId] = fixed
      ? [source, id]
      : (cariValue.split(':') as ['customer' | 'supplier', string]);
    if (!pickedSource || !pickedId) return;
    create.mutate({
      source: pickedSource,
      id: pickedId,
      date: new Date(date),
      voucherNo,
      description: description || undefined,
      currency: currency as 'TRY' | 'EUR' | 'GBP' | 'USD',
      direction,
      amount,
      tryValue: isTRY ? amount : tryValue ?? 0,
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('cariMovements.addMovement', 'Add Movement')}
            {fixed && counterpartyName ? ` — ${counterpartyName}` : ''}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {!fixed && (
            <div className="space-y-1.5">
              <Label className="text-xs">{t('cariMovements.cari', 'Cari')}</Label>
              <Combobox
                options={cariOptions}
                value={cariValue}
                onValueChange={setCariValue}
                placeholder={t('cariMovements.selectCari', 'Select a cari...')}
                searchPlaceholder={t('cariMovements.searchCari', 'Name or code...')}
                emptyText={t('cariMovements.noCari', 'No cari found')}
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs">{t('cariMovements.date', 'Date')}</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{t('cariMovements.voucherNo', 'Voucher No')}</Label>
              <Input value={voucherNo} onChange={(e) => setVoucherNo(e.target.value)} placeholder="2026..." />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">{t('cariMovements.description', 'Description')}</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs">{t('cariMovements.currency', 'Currency')}</Label>
              <select value={currency} onChange={(e) => setCurrency(e.target.value)} className={selectClass}>
                {CURRENCIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{t('cariMovements.direction', 'Type')}</Label>
              <select value={direction} onChange={(e) => setDirection(e.target.value as 'debit' | 'credit')} className={selectClass}>
                <option value="debit">{t('cariMovements.debit', 'Debit (Borç)')}</option>
                <option value="credit">{t('cariMovements.credit', 'Credit (Alacak)')}</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs">
                {t('cariMovements.amount', 'Amount')} ({currency})
              </Label>
              <NumberInput value={amount ?? ''} onValueChange={setAmount} decimalScale={2} placeholder="0.00" />
            </div>
            {!isTRY && (
              <div className="space-y-1.5">
                <Label className="text-xs">{t('cariMovements.tryValue', 'TL Value (₺)')}</Label>
                <NumberInput value={tryValue ?? ''} onValueChange={setTryValue} decimalScale={2} placeholder="0.00" />
              </div>
            )}
          </div>

          <p className="text-xs text-muted-foreground">
            {t('cariMovements.hint', 'Enter the amount on the chosen side — Debit (Borç) increases what they owe us, Credit (Alacak) what we owe them.')}
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('cariMovements.cancel', 'Cancel')}
          </Button>
          <Button onClick={submit} disabled={create.isPending || !canSubmit}>
            {create.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('cariMovements.save', 'Save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
